;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();export const DEPLOYED_ENVIRONMENTS = ["production", "demo", "staging"] as const;

/** Replay and profiling: customer-facing environments only. */
const FULL_TELEMETRY_ENVIRONMENTS = ["production", "demo"] as const;

export const LOG_LEVEL_SETTINGS = [
	"debug",
	"info",
	"warn",
	"error",
	"off",
] as const;

export type LogLevelSetting = (typeof LOG_LEVEL_SETTINGS)[number];

const LOG_LEVEL_RANK = {
	debug: 1,
	error: 4,
	fatal: 5,
	info: 2,
	trace: 0,
	warn: 3,
} as const;

export type EmittedLogLevel = keyof typeof LOG_LEVEL_RANK;

export type ObservabilityService = "browser" | "edge" | "web" | "tasks-worker";

export type ObservabilityRuntime = "browser" | "edge" | "nodejs";

export type LogSettings = {
	environment: string;
	logLevel: LogLevelSetting;
};

export type ObservabilityConfig = {
	environment: string;
	logLevel: LogLevelSetting;
	release: string | undefined;
	revision: string | undefined;
	service: ObservabilityService;
	traceSampleRate: number;
};

const DEVELOPMENT_ENVIRONMENT = "development";
const STAGING_ENVIRONMENT = "staging";
// `info` is a breadcrumb unless the level lets it through, so Issues keep
// their lead-up either way. Staging keeps `info` for debugging runs.
const DEPLOYED_DEFAULT_LOG_LEVEL: LogLevelSetting = "warn";
const STAGING_DEFAULT_LOG_LEVEL: LogLevelSetting = "info";
const LOCAL_DEFAULT_LOG_LEVEL: LogLevelSetting = "off";
const DEPLOYED_DEFAULT_TRACE_SAMPLE_RATE = 0.25;
const LOCAL_DEFAULT_TRACE_SAMPLE_RATE = 1;
const TASKS_WORKER_SERVICE_SUFFIX = "-tasks-worker";

function normalize(rawValue: string | undefined): string {
	return rawValue?.trim().toLowerCase() ?? "";
}

export function resolveEnvironment(rawValue: string | undefined): string {
	return normalize(rawValue) || DEVELOPMENT_ENVIRONMENT;
}

export function isDeployedEnvironment(environment: string): boolean {
	return (DEPLOYED_ENVIRONMENTS as readonly string[]).includes(environment);
}

export function isFullTelemetryEnvironment(environment: string): boolean {
	return (FULL_TELEMETRY_ENVIRONMENTS as readonly string[]).includes(
		environment,
	);
}

function isLogLevelSetting(value: string): value is LogLevelSetting {
	return (LOG_LEVEL_SETTINGS as readonly string[]).includes(value);
}

function getDefaultLogLevel({
	environment,
	runtime,
}: {
	environment: string;
	runtime: ObservabilityRuntime;
}): LogLevelSetting {
	if (!isDeployedEnvironment(environment)) {
		return LOCAL_DEFAULT_LOG_LEVEL;
	}
	// Every browser session would ship its own copy of each `info` log.
	if (runtime === "browser") {
		return DEPLOYED_DEFAULT_LOG_LEVEL;
	}
	return environment === STAGING_ENVIRONMENT
		? STAGING_DEFAULT_LOG_LEVEL
		: DEPLOYED_DEFAULT_LOG_LEVEL;
}

export function resolveLogLevel({
	environment,
	rawValue,
	runtime,
}: {
	environment: string;
	rawValue: string | undefined;
	runtime: ObservabilityRuntime;
}): LogLevelSetting {
	const normalizedValue = normalize(rawValue);
	if (isLogLevelSetting(normalizedValue)) {
		return normalizedValue;
	}
	return getDefaultLogLevel({ environment, runtime });
}

export function isLogLevelEnabled({
	level,
	threshold,
}: {
	level: EmittedLogLevel;
	threshold: LogLevelSetting;
}): boolean {
	if (threshold === "off") {
		return false;
	}
	return LOG_LEVEL_RANK[level] >= LOG_LEVEL_RANK[threshold];
}

function parseSampleRate(rawValue: string | undefined): number | undefined {
	const trimmedValue = rawValue?.trim();
	if (!trimmedValue) {
		return undefined;
	}
	const parsedValue = Number(trimmedValue);
	if (!Number.isFinite(parsedValue) || parsedValue < 0 || parsedValue > 1) {
		return undefined;
	}
	return parsedValue;
}

export function resolveTraceSampleRate({
	environment,
	rawValue,
}: {
	environment: string;
	rawValue: string | undefined;
}): number {
	return (
		parseSampleRate(rawValue) ??
		(isDeployedEnvironment(environment)
			? DEPLOYED_DEFAULT_TRACE_SAMPLE_RATE
			: LOCAL_DEFAULT_TRACE_SAMPLE_RATE)
	);
}

export function resolveRelease(
	rawValue: string | undefined,
): string | undefined {
	return rawValue?.trim() || undefined;
}

export function resolveService({
	cloudRunService,
	runtime,
}: {
	cloudRunService: string | undefined;
	runtime: ObservabilityRuntime;
}): ObservabilityService {
	if (runtime === "browser") {
		return "browser";
	}
	if (runtime === "edge") {
		return "edge";
	}
	return cloudRunService?.endsWith(TASKS_WORKER_SERVICE_SUFFIX)
		? "tasks-worker"
		: "web";
}

type RawSetting = { name: string; value: string | undefined };

// Literal `process.env.NEXT_PUBLIC_*` reads so Next.js inlines them in the
// browser bundle. Server variables win when both are set.
function readRawLogLevel(): RawSetting {
	if (process.env.SENTRY_LOG_LEVEL) {
		return { name: "SENTRY_LOG_LEVEL", value: process.env.SENTRY_LOG_LEVEL };
	}
	return {
		name: "NEXT_PUBLIC_SENTRY_LOG_LEVEL",
		value: process.env.NEXT_PUBLIC_SENTRY_LOG_LEVEL,
	};
}

function readRawTraceSampleRate(): RawSetting {
	if (process.env.SENTRY_TRACES_SAMPLE_RATE) {
		return {
			name: "SENTRY_TRACES_SAMPLE_RATE",
			value: process.env.SENTRY_TRACES_SAMPLE_RATE,
		};
	}
	return {
		name: "NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE",
		value: process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
	};
}

/**
 * Environment and log level only, so the logger can read it lazily. The logger
 * passes no runtime: `window` tells the browser apart, and server and edge
 * share their defaults.
 */
export function readLogSettings({
	runtime = typeof window === "undefined" ? "nodejs" : "browser",
}: {
	runtime?: ObservabilityRuntime;
} = {}): LogSettings {
	const environment = resolveEnvironment(
		process.env.APP_ENV || process.env.NEXT_PUBLIC_ENV,
	);
	return {
		environment,
		logLevel: resolveLogLevel({
			environment,
			rawValue: readRawLogLevel().value,
			runtime,
		}),
	};
}

/**
 * Each init file passes the runtime it is written for, so nothing has to
 * detect it (detection needs Node-only APIs the Edge bundle rejects).
 */
export function readObservabilityConfig({
	runtime,
}: {
	runtime: ObservabilityRuntime;
}): ObservabilityConfig {
	const logSettings = readLogSettings({ runtime });

	return {
		...logSettings,
		release: resolveRelease(
			process.env.SENTRY_RELEASE || process.env.NEXT_PUBLIC_SENTRY_RELEASE,
		),
		revision: resolveRelease(process.env.K_REVISION),
		service: resolveService({
			cloudRunService: process.env.K_SERVICE,
			runtime,
		}),
		traceSampleRate: resolveTraceSampleRate({
			environment: logSettings.environment,
			rawValue: readRawTraceSampleRate().value,
		}),
	};
}

export function listInvalidObservabilitySettings(): string[] {
	const invalidSettings: string[] = [];

	const logLevel = readRawLogLevel();
	const normalizedLogLevel = normalize(logLevel.value);
	if (normalizedLogLevel && !isLogLevelSetting(normalizedLogLevel)) {
		invalidSettings.push(
			`${logLevel.name}="${logLevel.value}" is not one of ${LOG_LEVEL_SETTINGS.join(", ")}. Using the default.`,
		);
	}

	const traceSampleRate = readRawTraceSampleRate();
	if (
		traceSampleRate.value?.trim() &&
		parseSampleRate(traceSampleRate.value) === undefined
	) {
		invalidSettings.push(
			`${traceSampleRate.name}="${traceSampleRate.value}" is not a number from 0 to 1. Using the default.`,
		);
	}

	return invalidSettings;
}
